import { getToken } from "@/utils/functions/getToken"
import { HoursAvailableGetBeautyInput } from "@okeys/shared-utils"
import { useMutation, useQuery } from "@tanstack/react-query"
import { customAxios } from "../utils/customAxios/customAxios"

/**
 * Mutation pour récupérer les heures disponibles d'un prestataire
 * Utilise POST pour une meilleure flexibilité avec les paramètres dynamiques
 */
export const useHoursAvailableMutation = () => {
  return useMutation({
    mutationFn: async (
      params: HoursAvailableGetBeautyInput
    ): Promise<string[]> => {
      try {
        const { prestatereId, date, duration, shopId } = params

        if (!prestatereId || !date || !duration || !shopId) {
          return []
        }

        const token = await getToken()

        // Utiliser POST avec les paramètres dans le body pour plus de flexibilité
        const response = await customAxios.post<{ availableHours: string[] }>(
          `/hours-available/search`,
          {
            prestatereId,
            date,
            duration,
            shopId,
          },
          {
            headers: {
              Authorization: token || "",
              "Content-Type": "application/json",
            },
          }
        )

        return response.data.availableHours || []
      } catch (err) {
        console.error("Error fetching available hours:", err)
        throw err
      }
    },
    onError: (error) => {
      console.error("Mutation error:", error)
    },
  })
}

/**
 * Hook pour récupérer les heures disponibles de tous les prestataires pour une date donnée
 * @param params - Paramètres contenant date, duration et shopId
 * @param options - Options pour la query
 * @returns Les heures disponibles de tous les prestataires
 */
export const useAllPrestatairesHoursAvailableQuery = (
  params: {
    date: string
    duration: string
    shopId: string
    prestatereIds: string[]
  },
  options?: {
    enabled?: boolean
    refetchInterval?: number
    staleTime?: number
  }
) => {
  const { data, isLoading, error, isError, refetch, isFetching } = useQuery({
    queryKey: [
      "allPrestatairesHoursAvailable",
      params.date,
      params.duration,
      params.shopId,
      params.prestatereIds,
    ],
    queryFn: async (): Promise<Record<string, string[]>> => {
      try {
        const { date, duration, shopId, prestatereIds } = params

        if (!date || !duration || !shopId || !prestatereIds.length) {
          return {}
        }

        const token = await getToken()

        // Récupérer les heures pour chaque prestataire en parallèle
        const promises = prestatereIds.map(async (prestatereId) => {
          try {
            const queryParams = new URLSearchParams({
              prestatereId,
              date,
              duration,
              shopId,
            })

            const response = await customAxios.get<{
              availableHours: string[]
            }>(`/hours-available/prestatere?${queryParams.toString()}`, {
              headers: {
                Authorization: token || "",
                "Content-Type": "application/json",
              },
            })

            return {
              prestatereId,
              hours: response.data.availableHours || [],
            }
          } catch (err) {
            console.error(
              `Error fetching hours for prestatere ${prestatereId}:`,
              err
            )
            return {
              prestatereId,
              hours: [],
            }
          }
        })

        const results = await Promise.all(promises)

        // Convertir en objet avec prestatereId comme clé
        return results.reduce((acc, { prestatereId, hours }) => {
          acc[prestatereId] = hours
          return acc
        }, {} as Record<string, string[]>)
      } catch (err) {
        console.error("Error fetching all prestataires hours:", err)
        throw err
      }
    },
    enabled:
      options?.enabled !== false &&
      !!params.date &&
      !!params.duration &&
      !!params.shopId &&
      params.prestatereIds.length > 0,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime || 1 * 60 * 1000, // 1 minute par défaut
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return {
    prestatairesHours: data || {},
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
  }
}
